import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export function PrivacyPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      {/* Header */}
      <div className="bg-white border-b border-[#E3DED2]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#6E7582] hover:text-[#0F1C2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-[#0F1C2E] mb-8">Privacy Policy</h1>

        <div className="prose prose-blue max-w-none">
          <p className="text-[#6E7582] mb-8">
            Last updated: January 2026
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">1. Introduction</h2>
            <p className="text-[#6E7582] mb-4">
              At Drive Without Fear, we take your privacy seriously. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our
              digital therapy platform. We are committed to protecting your personal data in
              accordance with the General Data Protection Regulation (GDPR) and other applicable
              data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">2. Data Controller</h2>
            <p className="text-[#6E7582] mb-4">
              Drive Without Fear is the data controller responsible for your personal data.
              Our data is stored and processed within the European Union (Ireland).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">3. Data We Collect</h2>
            <p className="text-[#6E7582] mb-4">
              We collect and process the following types of personal data:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>
                <strong>Account Information:</strong> Email address, name, and account credentials
              </li>
              <li>
                <strong>Onboarding Data:</strong> Self-assessment responses to personalize your
                therapy experience
              </li>
              <li>
                <strong>Progress Data:</strong> Video viewing history, anxiety ratings, and
                assessment completions
              </li>
              <li>
                <strong>Voice Memos:</strong> Audio recordings you choose to create and optionally
                share with your support person
              </li>
              <li>
                <strong>Payment Information:</strong> Transaction records (payment details are
                processed by Stripe and not stored on our servers)
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">4. How We Use Your Data</h2>
            <p className="text-[#6E7582] mb-4">
              We use your personal data for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>To provide and maintain our therapy platform</li>
              <li>To personalize your therapy experience based on your assessments</li>
              <li>To track your progress and provide relevant recommendations</li>
              <li>To facilitate communication with linked support persons</li>
              <li>To process payments and maintain billing records</li>
              <li>To send important service-related communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">5. Legal Basis for Processing</h2>
            <p className="text-[#6E7582] mb-4">
              We process your personal data based on:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>
                <strong>Contract Performance:</strong> Processing necessary to provide our services
              </li>
              <li>
                <strong>Legitimate Interests:</strong> Improving our services and ensuring security
              </li>
              <li>
                <strong>Consent:</strong> For optional features like voice memo sharing
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">6. Data Sharing</h2>
            <p className="text-[#6E7582] mb-4">
              We share your personal data only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>
                <strong>With Linked Support Persons:</strong> If you choose to link with a support person,
                they can access your progress data and any voice memos you share
              </li>
              <li>
                <strong>Payment Processing:</strong> Stripe processes payment information
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to protect our rights
              </li>
            </ul>
            <p className="text-[#6E7582] mb-4">
              We do not sell your personal data to third parties. We do not use your data for
              advertising purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">7. Data Security</h2>
            <p className="text-[#6E7582] mb-4">
              We implement appropriate technical and organizational measures to protect your
              personal data, including:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Row-level security policies ensuring users can only access their own data</li>
              <li>Regular security assessments and updates</li>
              <li>Secure data storage within the EU (Ireland)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">8. Your Rights</h2>
            <p className="text-[#6E7582] mb-4">
              Under GDPR, you have the following rights:
            </p>
            <ul className="list-disc pl-6 text-[#6E7582] mb-4 space-y-2">
              <li>
                <strong>Right of Access:</strong> Request a copy of your personal data
              </li>
              <li>
                <strong>Right to Rectification:</strong> Request correction of inaccurate data
              </li>
              <li>
                <strong>Right to Erasure:</strong> Request deletion of your personal data
              </li>
              <li>
                <strong>Right to Restrict Processing:</strong> Request limitation of data processing
              </li>
              <li>
                <strong>Right to Data Portability:</strong> Receive your data in a structured format
              </li>
              <li>
                <strong>Right to Object:</strong> Object to processing based on legitimate interests
              </li>
            </ul>
            <p className="text-[#6E7582] mb-4">
              You can exercise these rights through your account settings or by contacting us
              directly. We will respond to your request within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">9. Data Retention</h2>
            <p className="text-[#6E7582] mb-4">
              We retain your personal data for as long as your account is active or as needed to
              provide our services. After account deletion, we retain anonymized data for
              analytics purposes only. You can request complete data deletion at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">10. Cookies</h2>
            <p className="text-[#6E7582] mb-4">
              We use essential cookies only to maintain your session and provide core functionality.
              We do not use tracking or advertising cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">11. Changes to This Policy</h2>
            <p className="text-[#6E7582] mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any
              significant changes via email or through the platform. The "Last updated" date
              indicates when this policy was last revised.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F1C2E] mb-4">12. Contact Us</h2>
            <p className="text-[#6E7582] mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights,
              please contact us:
            </p>
            <p className="text-[#6E7582] mb-2">
              Email: privacy@drivewithoutfear.com
            </p>
            <p className="text-[#6E7582]">
              You also have the right to lodge a complaint with the Irish Data Protection
              Commission if you believe your rights have been violated.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
